import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Building2, Layers, FileText, CreditCard, LogOut } from "lucide-react";
import { Logo } from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  ["clinics", "", "Clinics", Building2],
  ["plans", "plans", "Plans & pricing", Layers],
  ["content", "content", "Content & SEO", FileText],
  ["payments", "payments", "Payment settings", CreditCard],
];

export const SuperHead = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1" data-testid="super-title">{title}</h1>
    </div>
    <div className="flex gap-2">{children}</div>
  </div>
);

export default function SuperLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const cls = ({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#1E3A34] text-white" : "text-slate-600 hover:bg-muted"}`;
  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="h-16 flex items-center px-5 border-b border-border"><Logo to="/super" /></div>
        <p className="px-5 pt-4 pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent">Platform admin</p>
        <nav className="flex-1 px-3 space-y-1">
          {LINKS.map(([k, path, label, I]) => (
            <NavLink key={k} to={`/super/${path}`} end={path === ""} className={cls} data-testid={`super-sidebar-link-${k}`}><I className="h-4 w-4" />{label}</NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-muted" data-testid="super-logout-button">
            <LogOut className="h-4 w-4" />Log out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="h-16 sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border flex items-center px-4 sm:px-8 gap-4">
          <div className="md:hidden"><Logo to="/super" /></div>
          <p className="hidden md:block text-sm text-muted-foreground">Signed in as <span className="text-foreground font-medium">{user?.email}</span></p>
        </header>
        <nav className="md:hidden flex gap-1 overflow-x-auto px-4 py-2 border-b border-border bg-card">
          {LINKS.map(([k, path, label]) => (
            <NavLink key={k} to={`/super/${path}`} end={path === ""} data-testid={`super-mobile-link-${k}`}
              className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-[#1E3A34] text-white" : "text-slate-600"}`}>{label}</NavLink>
          ))}
        </nav>
        <main className="px-4 sm:px-8 py-8 fade-up"><Outlet /></main>
      </div>
    </div>
  );
}
